import { useEffect, useCallback, useState } from 'react';
import { useIntl } from '@leagueplatform/locales';
import { IframeModal } from 'components/modals/common/iframe-modal.component';
import { BookAppointmentConfirmCloseModal } from 'components/modals/book-appointment-confirm-close-modal.component';
import { useIframeFlowStatus } from 'hooks/use-iframe-flow-status/use-iframe-flow-status.hook';
import { useIframeKeepAlive } from 'hooks/use-iframe-keep-alive/use-iframe-keep-alive.hook';
import {
  getAllAppointmentIframeAllowedOrigins,
  isMockAppointmentIframeEnabled,
} from 'common/env';

const MOCK_BOOKING_IFRAME_PATH = '/mock-book-appointment-iframe.html';

function getValidatedIframeSrc(src: string): string {
  if (src === 'about:blank' || src.startsWith('/')) return src;
  try {
    const { origin } = new URL(src);
    const allowed = getAllAppointmentIframeAllowedOrigins();
    return allowed.includes(origin) ? src : 'about:blank';
  } catch {
    return 'about:blank';
  }
}

export const AppointmentIframeModal = ({
  bookingLink,
  onClose,
}: {
  bookingLink?: string;
  onClose: (hasCompleted: boolean) => void;
}) => {
  const { formatMessage } = useIntl();
  const [isBookAppointmentOpen, setIsBookAppointmentOpen] = useState(true);
  const [isConfirmExitOpen, setIsConfirmExitOpen] = useState(false);
  const { status } = useIframeFlowStatus();
  const hasCompleted = status !== 'pending';

  const iframeTitle = formatMessage({ id: 'STR_BOOK_APPOINTMENT' });
  const iframeSrc = isMockAppointmentIframeEnabled()
    ? MOCK_BOOKING_IFRAME_PATH
    : getValidatedIframeSrc(bookingLink ?? 'about:blank');

  useIframeKeepAlive({ enabled: isBookAppointmentOpen });

  const handleCloseAttempt = useCallback(() => {
    if (hasCompleted) {
      setIsBookAppointmentOpen(false);
      onClose(true);
      return;
    }
    setIsConfirmExitOpen(true);
  }, [hasCompleted, onClose]);

  useEffect(() => {
    if (status !== 'completed-go-home') return;
    handleCloseAttempt();
  }, [status, handleCloseAttempt]);

  return (
    <>
      <IframeModal
        open={isBookAppointmentOpen}
        onOpenChange={(open) => {
          if (open) {
            setIsBookAppointmentOpen(true);
            return;
          }
          handleCloseAttempt();
        }}
      >
        <iframe
          id="bookAppointmentIframeModal"
          height="100%"
          width="100%"
          title={iframeTitle}
          src={iframeSrc}
        />
      </IframeModal>
      <BookAppointmentConfirmCloseModal
        isOpen={isConfirmExitOpen}
        onCancel={() => {
          setIsConfirmExitOpen(false);
          setIsBookAppointmentOpen(true);
        }}
        onConfirm={() => {
          setIsConfirmExitOpen(false);
          setIsBookAppointmentOpen(false);
          onClose(false);
        }}
      />
    </>
  );
};
